import React from "react";
import {
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { Dialog } from "../../../../../../../components/Dialogs/Dialog";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { fetchCommAutomobileVehicleListAsync } from "../../../../../../../redux/commAutoPolicy/commAutoVehicle/commAutoVehicleActions";
import { fetchCommAutomobileCustomerInfoAsync } from "../../../../../../../redux/commAutoPolicy/commAutoBasicDetails/commAutoBasicDetailsActions";
import { ICommAutomobileVehicleInformation } from "../../../../../../../redux/commAutoPolicy/commAutoVehicle/commAutoVehicle.types";
import {
  generateExcelSheet,
  ICSVColumnsFormat,
  ICSVDataFormat,
} from "../../../../../../../components/TableV2/preDefinedPlugins/DataTableV2CSVExport/DataTableV2CSVExport";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { LoadState } from "../../../../../../../constants/enums";
import { CSS_CONSTANTS } from "../../../../../../../constants/cssConstants";
import {
  formatDate,
  formatDateWithTime,
} from "../../../../../../../helpers/convertDate";

// create_ts / insert_ts / effective_date are populated by the backend on each
// record but are not part of the typed vehicle interface, so read them through
// this helper type.
type IVehicleWithTs = ICommAutomobileVehicleInformation & {
  create_ts?: string | null;
  insert_ts?: string | null;
  effective_date?: string | null;
};

interface ICommAuto21BDialogProps {
  open: boolean;
  onClose: () => void;
  customerPolicyId: number;
  onSuccess?: () => void;
}

const SCHEDULE = "21b_schedule";
const CHANGES = "21b_changes";

export const CommAuto21BDialog: React.FC<ICommAuto21BDialogProps> = (props) => {
  const { open, onClose, customerPolicyId, onSuccess } = props;
  const [loading, setLoading] = React.useState(false);
  const [selectedType, setSelectedType] = React.useState<string>(SCHEDULE);
  const dispatch = useDispatchWrapper();

  const { vehiclesList, vehiclesListLoading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.vehicle.VehicleInformation
  );
  const { customerInfo } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.basicDetails
  );

  const effectiveDate = customerInfo.data.effective_date;
  const expiryDate = customerInfo.data.expiry_date;

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchCommAutomobileVehicleListAsync(customerPolicyId));
      dispatch(fetchCommAutomobileCustomerInfoAsync(customerPolicyId));
    }
  }, [customerPolicyId, dispatch]);

  // For "21b changes" only show vehicles whose effective date falls within the
  // policy effective and expiry dates. For "21b schedule" show every vehicle.
  const displayedVehicles = React.useMemo(() => {
    if (selectedType !== CHANGES) {
      return vehiclesList;
    }
    if (!effectiveDate || !expiryDate) {
      return vehiclesList;
    }
    const from = moment(effectiveDate).startOf("day");
    const to = moment(expiryDate).endOf("day");
    return vehiclesList.filter((vehicle) => {
      const vehicleEffectiveDate = (vehicle as IVehicleWithTs).effective_date;
      if (!vehicleEffectiveDate) {
        return false;
      }
      const effective = moment(vehicleEffectiveDate);
      return (
        effective.isValid() && effective.isBetween(from, to, undefined, "[]")
      );
    });
  }, [selectedType, vehiclesList, effectiveDate, expiryDate]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const rows = displayedVehicles.map((vehicle) => {
        const typed = vehicle as IVehicleWithTs;
        const isActive = (vehicle.status || "").toUpperCase() === "ACTIVE";
        return {
          status: isActive ? "ACTIVE" : "DELETED",
          unit_number: vehicle.unit_number ?? "",
          year: vehicle.year ?? "",
          make: vehicle.make ?? "",
          model: vehicle.model ?? "",
          vin: vehicle.vin ?? "",
          body_type: vehicle.body_type ?? "",
          description: vehicle.vehicle_type ?? "",
          gross_vehicle_weight: vehicle.gross_vehicle_weight ?? "",
          fleet_identity: vehicle.fleet_identity ?? "",
          effective_date: typed.effective_date ?? "",
        } as unknown as ICSVDataFormat;
      });

      const columns: ICSVColumnsFormat[] = [
        { columnName: "Status", fieldName: "status", width: 15 },
        { columnName: "Unit Number", fieldName: "unit_number", width: 15 },
        { columnName: "Year", fieldName: "year", width: 10 },
        { columnName: "Make", fieldName: "make", width: 15 },
        { columnName: "Model", fieldName: "model", width: 15 },
        { columnName: "VIN", fieldName: "vin", width: 20 },
        { columnName: "Body Type", fieldName: "body_type", width: 15 },
        { columnName: "Description", fieldName: "description", width: 20 },
        {
          columnName: "Gross Vehicle Weight",
          fieldName: "gross_vehicle_weight",
          width: 20,
        },
        { columnName: "Field Identity", fieldName: "fleet_identity", width: 15 },
        { columnName: "Effective Date", fieldName: "effective_date", width: 18 },
      ];

      const fileName =
        selectedType === CHANGES
          ? "CommAuto-21B-Changes"
          : "CommAuto-21B-Schedule";

      await generateExcelSheet(rows, columns, fileName);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isLoadingVehicles = vehiclesListLoading === LoadState.InProgress;

  return (
    <Dialog
      open={open}
      title="OPCF-21B (Vehicle List)"
      onClose={onClose}
      size="xl"
      fullWidth
      actions={[
        {
          type: "button",
          label: "Close",
          variant: "text",
          onClick: onClose,
        },
        {
          type: "button",
          label: "Download Excel",
          variant: "contained",
          disabled: loading || displayedVehicles.length === 0,
          onClick: handleDownloadExcel,
        },
      ]}
    >
      <Box sx={{ py: 2 }}>
        <Typography
          variant="body1"
          sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE, mb: 2 }}
        >
          The 21B form is used for reporting changes to commercial automobile
          fleet vehicles. Review the vehicle list below and click "Download
          Excel" to export it.
        </Typography>
        <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
          Select type:
        </Typography>
        <FormControl>
          <RadioGroup
            row
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <FormControlLabel
              value={SCHEDULE}
              control={<Radio />}
              label="21b Schedule"
            />
            <FormControlLabel
              value={CHANGES}
              control={<Radio />}
              label="21b Changes"
            />
          </RadioGroup>
        </FormControl>

        {selectedType === CHANGES && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: CSS_CONSTANTS.LABEL_VALUE_SIZE, mb: 1 }}
          >
            Showing vehicles added between{" "}
            <strong>
              {effectiveDate
                ? moment(effectiveDate).format("DD/MM/YYYY")
                : "--"}
            </strong>{" "}
            and{" "}
            <strong>
              {expiryDate ? moment(expiryDate).format("DD/MM/YYYY") : "--"}
            </strong>
            .
          </Typography>
        )}

        <VehiclePreviewTable
          vehicles={displayedVehicles}
          loading={isLoadingVehicles}
        />
      </Box>
    </Dialog>
  );
};

interface IVehiclePreviewTableProps {
  vehicles: ICommAutomobileVehicleInformation[];
  loading: boolean;
}

const COLUMNS = [
  "Status",
  "Unit Number",
  "Year",
  "Make",
  "Model",
  "VIN",
  "Body Type",
  "Description",
  "Gross Vehicle Weight",
  "Fleet Identity",
  "Effective Date",
  "Created At",
  "Inserted At",
];

const VehiclePreviewTable: React.FC<IVehiclePreviewTableProps> = ({
  vehicles,
  loading,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body1" color="textSecondary">
          No vehicle records found for this selection.
        </Typography>
      </Box>
    );
  }

  const renderStatus = (status: string) => {
    const isActive = (status || "").toUpperCase() === "ACTIVE";
    // Inactive vehicles are shown as "DELETED" in both the list and the Excel.
    return (
      <Chip
        size="small"
        label={isActive ? "ACTIVE" : "DELETED"}
        color={isActive ? "success" : "error"}
        sx={{ color: "#fff" }}
      />
    );
  };

  const display = (value: string | null | undefined) =>
    value !== null && value !== undefined && value !== "" ? value : "--";

  return (
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "45vh" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column} sx={{ fontWeight: 600 }}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicles.map((vehicle, index) => (
            <TableRow key={vehicle.incremental_number ?? index} hover>
              <TableCell>{renderStatus(vehicle.status)}</TableCell>
              <TableCell>{display(vehicle.unit_number)}</TableCell>
              <TableCell>{display(vehicle.year)}</TableCell>
              <TableCell>{display(vehicle.make)}</TableCell>
              <TableCell>{display(vehicle.model)}</TableCell>
              <TableCell>{display(vehicle.vin)}</TableCell>
              <TableCell>{display(vehicle.body_type)}</TableCell>
              <TableCell>{display(vehicle.vehicle_type)}</TableCell>
              <TableCell>{display(vehicle.gross_vehicle_weight)}</TableCell>
              <TableCell>{display(vehicle.fleet_identity)}</TableCell>
              <TableCell>
                {(vehicle as IVehicleWithTs).effective_date
                  ? formatDate(
                      (vehicle as IVehicleWithTs).effective_date ?? null
                    )
                  : "--"}
              </TableCell>
              <TableCell>
                {(vehicle as IVehicleWithTs).create_ts
                  ? formatDateWithTime(
                      (vehicle as IVehicleWithTs).create_ts ?? null
                    )
                  : "--"}
              </TableCell>
              <TableCell>
                {(vehicle as IVehicleWithTs).insert_ts
                  ? formatDateWithTime(
                      (vehicle as IVehicleWithTs).insert_ts ?? null
                    )
                  : "--"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
